// This extracts the set of fields that match Condition
// https://medium.com/dailyjs/typescript-create-a-condition-based-subset-types-9d902cea5b8c
export type Fields<Type, Condition> = string &
  {
    [Key in keyof Required<Type>]: [Condition] extends [Type[Key]]
      ? Key
      : never;
  }[keyof Type];

export type Optional<Type, Key extends keyof Type> = Partial<Pick<Type, Key>> &
  Omit<Type, Key>;

type NestedPaths<Type, Condition, Prefix extends string = ""> =
  // don't allow "" as a path
  | ([Condition] extends [Type] ? ("" extends Prefix ? never : Prefix) : never)
  | (Type extends (infer Value)[]
      ? NestedPaths<Value, Condition, `${Prefix}[${number}]`>
      : Type extends object
        ? {
            [Key in keyof Type]: Key extends string
              ? NestedPaths<
                  Type[Key],
                  Condition,
                  // avoid leading "."; can't append it to Prefix in case of array indexing
                  `${Prefix}${"" extends Prefix ? "" : "."}${Key}`
                >
              : never;
          }[keyof Type]
        : never);

/**
 * Extract dot-notated paths to nested properties of object Type that meet Condition
 * Example:
 *
 * type T = Paths<
 *   {
 *     abc: string;
 *     def: {
 *       ghi: string;
 *       xyz: number;
 *     }
 *   },
 *   string
 * >
 * // T === "abc" | "def.ghi"
 */
// don't export the recursively Prefix-ed impl version
// the extends infer wrapper prevents "excessively deep" errors
// https://x.com/solinvictvs/status/1671507561143476226?s=20
export type Paths<Type, Condition> = Type extends infer T
  ? string & NestedPaths<T, Condition>
  : string;

/**
 * Require all fields to exist, but optional fields may still have a value of undefined
 * https://medium.com/terria/typescript-transforming-optional-properties-to-required-properties-that-may-be-undefined-7482cb4e1585
 */
export type Complete<Type> = {
  [Key in keyof Required<Type>]: Type[Key];
};

export type AllPartial<Type> = Type extends object
  ? {
      [Key in keyof Type]: Partial<AllPartial<Type[Key]>>;
    }
  : Type extends (infer Value)[]
    ? Partial<AllPartial<Value>[]>
    : Type;

/**
 * add manual indexing for formik name support
 * e.g., name={`ingredients[${ingredient.index}].name`}
 */
export type Indexed<Shape> = Shape & {
  index: number;
};

export type On = true;
export type Off = false | undefined;
export type Toggle = On | Off;

export function isDefinedEntry<T>(
  tuple: [string, T | undefined],
): tuple is [string, T] {
  return tuple[1] != null;
}

// Distributes `Omit` over union members so variant-specific fields survive.
export type DistributiveOmit<T, K extends keyof any> = T extends unknown
  ? Omit<T, K>
  : never;
